import { useEffect, useState, useCallback, useRef } from 'react';
import socketServer, { getConnectionState, forceReconnect, disconnectSocket } from '../services/MainSocket';

export const useSocketConnection = () => {
  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    isConnecting: false,
    reconnectAttempts: 0,
    lastReconnectAttempt: null,
    connectionError: null
  });

  const [rooms, setRooms] = useState([]);
  const reconnectTimeoutRef = useRef(null);
  const manualReconnectAttemptsRef = useRef(0);
  const maxManualReconnectAttempts = 5;

  // Update connection state
  const updateConnectionState = useCallback(() => {
    const state = getConnectionState();
    setConnectionState(state);
  }, []);

  // Join a room and track it
  const joinRoom = useCallback((roomId, roomName, email) => {
    if (!socketServer.connected) {
      console.warn('Socket not connected, cannot join room:', roomName);
      return false;
    }

    try {
      socketServer.emit('joinClientChannel', roomId, roomName, email);
      
      // Track the room
      setRooms(prevRooms => {
        const existingRoom = prevRooms.find(room => room.roomId === roomId);
        if (!existingRoom) {
          return [...prevRooms, { roomId: roomId, name: roomName, email, joinedAt: new Date().toISOString() }];
        }
        return prevRooms;
      });

      console.log('✅ Joined room:', roomName);
      return true;
    } catch (error) {
      console.error('❌ Failed to join room:', roomName, error);
      return false;
    }
  }, []);

  // Leave a room
  const leaveRoom = useCallback((roomName) => {
    try {
      socketServer.emit('leaveClientChannel', roomName);
      
      setRooms(prevRooms => prevRooms.filter(room => room.name !== roomName));
      console.log('👋 Left room:', roomName);
    } catch (error) {
      console.error('❌ Failed to leave room:', roomName, error);
    }
  }, []);

  // Rejoin all rooms after reconnection
  const rejoinRooms = useCallback(() => {
    if (rooms.length === 0) return;

    console.log('🔄 Rejoining rooms after reconnection...', rooms);
    rooms.forEach(room => {
      setTimeout(() => {
        joinRoom(room.roomId, room.name, room.email);
      }, 100); // Small delay to avoid overwhelming the server
    });
  }, [rooms, joinRoom]);

  // Manual reconnect with exponential backoff
  const manualReconnect = useCallback(() => {
    if (manualReconnectAttemptsRef.current >= maxManualReconnectAttempts) {
      console.error('💥 Max manual reconnection attempts reached');
      return;
    }

    manualReconnectAttemptsRef.current++;
    const delay = Math.min(1000 * Math.pow(2, manualReconnectAttemptsRef.current - 1), 10000);
    
    console.log(`🔄 Manual reconnection attempt ${manualReconnectAttemptsRef.current}/${maxManualReconnectAttempts} in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      forceReconnect();
    }, delay);
  }, []);

  // Reset manual reconnect attempts on successful connection
  const resetManualReconnectAttempts = useCallback(() => {
    manualReconnectAttemptsRef.current = 0;
  }, []);

  // Setup connection event listeners
  useEffect(() => {
    const handleConnect = () => {
      console.log('✅ Socket connected in hook');
      updateConnectionState();
      resetManualReconnectAttempts();
      
      // Rejoin rooms after successful connection
      setTimeout(() => {
        rejoinRooms();
      }, 500);
    };

    const handleDisconnect = (reason) => {
      console.log('❌ Socket disconnected in hook:', reason);
      updateConnectionState();
    };

    const handleReconnect = (attemptNumber) => {
      console.log(`🔄 Socket reconnected in hook after ${attemptNumber} attempts`);
      updateConnectionState();
      resetManualReconnectAttempts();
      
      // Rejoin rooms after reconnection
      setTimeout(() => {
        rejoinRooms();
      }, 500);
    };

    const handleReconnectAttempt = (attemptNumber) => {
      console.log(`🔄 Socket reconnection attempt ${attemptNumber} in hook`);
      updateConnectionState();
    };

    const handleReconnectFailed = () => {
      console.error('💥 Socket reconnection failed in hook');
      updateConnectionState();
      
      // Try manual reconnect if auto-reconnect failed
      if (manualReconnectAttemptsRef.current < maxManualReconnectAttempts) {
        manualReconnect();
      }
    };

    const handleConnectionError = (error) => {
      console.error('🚨 Socket connection error in hook:', error);
      updateConnectionState();
    };

    // Listen for custom reconnection failed event
    const handleReconnectionFailed = () => {
      console.log('💥 Received reconnection failed event');
      if (manualReconnectAttemptsRef.current < maxManualReconnectAttempts) {
        manualReconnect();
      }
    };

    // Add event listeners
    socketServer.on('connect', handleConnect);
    socketServer.on('disconnect', handleDisconnect);
    socketServer.on('reconnect', handleReconnect);
    socketServer.on('reconnect_attempt', handleReconnectAttempt);
    socketServer.on('reconnect_failed', handleReconnectFailed);
    socketServer.on('connect_error', handleConnectionError);
    
    if (typeof window !== 'undefined') {
      window.addEventListener('socketReconnectionFailed', handleReconnectionFailed);
    }

    // Initial connection state
    updateConnectionState();

    // Connect if not already connected
    if (!socketServer.connected) {
      console.log('🔌 Connecting socket from hook...');
      socketServer.connect();
    }

    // Cleanup
    return () => {
      socketServer.off('connect', handleConnect);
      socketServer.off('disconnect', handleDisconnect);
      socketServer.off('reconnect', handleReconnect);
      socketServer.off('reconnect_attempt', handleReconnectAttempt);
      socketServer.off('reconnect_failed', handleReconnectFailed);
      socketServer.off('connect_error', handleConnectionError);
      
      if (typeof window !== 'undefined') {
        window.removeEventListener('socketReconnectionFailed', handleReconnectionFailed);
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [updateConnectionState, rejoinRooms, manualReconnect, resetManualReconnectAttempts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Connection state
    isConnected: connectionState.isConnected,
    isConnecting: connectionState.isConnecting,
    reconnectAttempts: connectionState.reconnectAttempts,
    lastReconnectAttempt: connectionState.lastReconnectAttempt,
    connectionError: connectionState.connectionError,
    
    // Room management
    rooms,
    joinRoom,
    leaveRoom,
    
    // Connection management
    manualReconnect,
    forceReconnect,
    disconnectSocket,
    
    // Utility
    socket: socketServer
  };
}; 